import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { BlogPost } from "@tih/shared";
import { ApiError, fetchBlogPost } from "../api/client";
import { renderMarkdown } from "../lib/markdown";

function expandYoutubeMarkers(html: string): string {
  return html.replace(
    /\[\[tih-youtube:([\w-]{11})(?::(\d+))?(?::(\d+))?\]\]/g,
    (_m, videoId: string, start?: string, end?: string) => {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const q = params.toString();
      const src = `https://www.youtube.com/embed/${videoId}${q ? `?${q}` : ""}`;
      return `<div class="blog-youtube-embed"><iframe src="${src}" title="YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`;
    },
  );
}


export function BlogPostPage() {
  const { slug = "" } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setError(null);
    setNotFound(false);
    fetchBlogPost(slug)
      .then(setPost)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(String(e?.message ?? e));
      });
  }, [slug]);

  const html = useMemo(() => {
    if (!post) return "";
    const raw = post.bodyFormat === "BLOCKS" ? post.body : renderMarkdown(post.body);
    return expandYoutubeMarkers(raw);
  }, [post]);

  if (notFound) {
    return (
      <section className="section" style={{ textAlign: "center", paddingTop: 160 }}>
        <h2 className="sec-title">Post not found</h2>
        <p style={{ opacity: 0.75, marginBottom: 32 }}>That post may have been moved or unpublished.</p>
        <Link to="/blog" className="btn btn-primary">Back to the journal</Link>
      </section>
    );
  }

  if (error) return <section className="section"><div className="form-error">{error}</div></section>;
  if (!post) return <section className="section"><div style={{ opacity: 0.5 }}>Loading…</div></section>;

  return (
    <article className="section blog-post">
      <Link to="/blog" className="blog-back">← All posts</Link>
      <div className="blog-post-meta">
        {post.publishedAt && (
          <time>{new Date(post.publishedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</time>
        )}
        <span>·</span>
        <span>{post.authorName}</span>
      </div>
      <h1 className="blog-post-title">{post.title}</h1>
      {post.excerpt && <p className="blog-post-excerpt">{post.excerpt}</p>}
      <div className="blog-post-body" dangerouslySetInnerHTML={{ __html: html }} />
    </article>
  );
}
